import React, { useContext, useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  ChartArea,
  Interaction,
} from 'chart.js'
import dayjs from 'dayjs'
import { ContextData } from '@floating-ui/react-dom-interactions'
// @ts-ignore
import { CrosshairPlugin, Interpolate } from 'chartjs-plugin-crosshair'
import { ThemeContext } from '../../context/ThemeProvider'
import { Skeleton } from '../skeleton/Skeleton'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
)
ChartJS.defaults.font.family = 'arial'
// @ts-ignore
Interaction.modes.interpolate = Interpolate

const options = (isDark?: boolean) => ({
  responsive: true,
  elements: {
    line: {
      tension: 0,
    },
    point: {
      radius: 2,
    },
  },
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
      labels: {
        color: isDark ? '#E0E0E0' : 'black',
        textShadow: isDark ? '0 0 5px rgba(255,255,255,0.3)' : 'none',
      },
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
    },
  },
  scales: {
    x: {
      ticks: {
        color: isDark ? '#E0E0E0' : 'black',
        font: {
          weight: 'bold' as const,
        },
      },
      grid: {
        color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
      },
    },
    y: {
      ticks: {
        color: isDark ? '#E0E0E0' : 'black',
        font: {
          weight: 'bold' as const,
        },
      },
      grid: {
        color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
      },
    },
  },
})

const getMidnightGradient = (ctx: ContextData, chartArea: ChartArea) => {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.addColorStop(0, 'rgba(100, 100, 200, 0.9)') // Brighter blue
  gradient.addColorStop(0.8, 'rgba(120, 100, 220, 0.6)') // Lighter blue
  gradient.addColorStop(1, 'rgba(150, 120, 240, 0.4)') // Even lighter blue
  return gradient
}

const getCrimsonSunsetGradient = (ctx: ContextData, chartArea: ChartArea) => {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.addColorStop(0, 'rgba(255, 80, 80, 0.9)') // Brighter red
  gradient.addColorStop(0.5, 'rgba(255, 120, 80, 0.6)') // Lighter orange-red
  gradient.addColorStop(1, 'rgba(255, 160, 80, 0.4)') // Even lighter orange
  return gradient
}

const getEmeraldGlowGradient = (ctx: ContextData, chartArea: ChartArea) => {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.addColorStop(0, 'rgba(50, 200, 50, 0.9)') // Brighter green
  gradient.addColorStop(0.7, 'rgba(100, 220, 100, 0.6)') // Lighter green
  gradient.addColorStop(1, 'rgba(150, 240, 150, 0.4)') // Even lighter green
  return gradient
}

const getCyberCyanGradient = (ctx: ContextData, chartArea: ChartArea) => {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.addColorStop(0, 'rgba(50, 200, 200, 0.9)') // Brighter cyan
  gradient.addColorStop(0.6, 'rgba(100, 220, 220, 0.6)') // Lighter cyan
  gradient.addColorStop(1, 'rgba(150, 240, 240, 0.4)') // Even lighter cyan
  return gradient
}

const getIndigoDreamGradient = (ctx: ContextData, chartArea: ChartArea) => {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.addColorStop(0, 'rgba(130, 50, 220, 0.9)') // Brighter purple
  gradient.addColorStop(0.5, 'rgba(160, 100, 240, 0.6)') // Lighter purple
  gradient.addColorStop(1, 'rgba(190, 150, 255, 0.4)') // Even lighter purple
  return gradient
}

const gradients = {
  midnight: getMidnightGradient,
  'crimson-sunset': getCrimsonSunsetGradient,
  'emerald-glow': getEmeraldGlowGradient,
  'cyber-cyan': getCyberCyanGradient,
  'indigo-dream': getIndigoDreamGradient,
}

const AreaChart = ({
  data,
  yKey,
  isLoading = false,
  isPreviousData = false,
  borderColor,
  customOptions,
  xKey,
  gradient,
}: {
  data: Array<any>
  yKey: string
  isLoading?: boolean
  isPreviousData?: boolean
  borderColor?: string
  customOptions?: Record<string, unknown>
  xKey?: string
  gradient: keyof typeof gradients
}) => {
  const { theme } = useContext(ThemeContext) // Access the theme from ThemeContext
  const isDark = theme === 'dark'
  const labels = useMemo(() => {
    if (xKey) return Array.from(new Set(data.map((d) => d[xKey])))
    else return Array.from(new Set(data.map((v) => dayjs(v.date).format("MMM D, 'YY"))))
  }, [xKey, data])

  const chartData = {
    labels,
    datasets: [
      {
        label: yKey,
        data: labels.map((l) => {
          const entry = data.find((d) =>
            xKey ? d[xKey] === l : dayjs(d.date).format("MMM D, 'YY") === l,
          ) ?? {
            [yKey]: 0,
          }
          return entry[yKey]
        }),
        borderColor: borderColor,
        fill: true,
        borderWidth: 0,
        datalabels: {
          display: false,
        },
        backgroundColor: (context: any) => {
          const chart = context.chart
          const { ctx, chartArea } = chart

          if (!chartArea) return

          return gradients[gradient](ctx, chartArea)
        },
      },
    ],
  }

  return (
    <div className={'relative h-full w-full '}>
      {/* {isPreviousData && (
        <div className={'absolute top-0 left-0 w-full h-full bg-white opacity-50'} />
      )} */}
      <Line options={{ ...options(isDark) }} data={chartData} />
    </div>
  )
}

export default AreaChart
